import React, { useRef, useState, useEffect } from "react";
import * as Yup from "yup";
import {
  ScopeCertificateDetails,
  StandardDetails,
} from "../../../sc-models/scope-certificate-details-dto";
import FormBuilder from "../../../../../../shared/components/form-builder/form-builder";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import { useScStore } from "../../../../../../store/scStandard.store";
import { useParams } from "react-router-dom";
import { notify } from "../../../../../../shared/utils/notify.utills";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import { InputAdornment } from "@mui/material";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";

interface ScopeCertificateDetailsFormProps {
  formRef: any;
  detailsData: ScopeCertificateDetails;
  setDetailsData: React.Dispatch<React.SetStateAction<ScopeCertificateDetails>>;
  // standardData: StandardDetails[];
  // setStandardsData: React.Dispatch<React.SetStateAction<StandardDetails[]>>;
  SC_ID?: string;
  isAmendment?: boolean;
  scValidFromDate?: string | number;
}

const ScopeCertificateDetailsForm: React.FC<
  ScopeCertificateDetailsFormProps
> = ({
  formRef,
  detailsData,
  setDetailsData,
  SC_ID,
  isAmendment,
  scValidFromDate,
}) => {
  const { editSCInfo, scStandards } = useScStore();
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === "submited-sc";
  // Check if scValidFromDate is in the future
  const isFutureDate =
    scValidFromDate && new Date(String(scValidFromDate)) > new Date();

  // State for dropdown options
  const [statusOptions, setStatusOptions] = useState<OptionType[]>([]);
  const [suspensionReasonOptions, setSuspensionReasonOptions] = useState<
    OptionType[]
  >([]);
  const [loading, setLoading] = useState(true);
  const { cbLicensingCode } = useTransactionCertificateStore();
  const shortCode = cbLicensingCode?.replace(/^CB-/, "") || "";

  // Commented out hardcoded options - now using API calls
  // const allStatusOptions: OptionType[] = [
  //   { label: "Issued (I)", value: "I" }, // Represents "Issued"
  //   { label: "Valid (V)", value: "V" }, // Represents "Valid"
  //   { label: "Expired (E)", value: "E" }, // Represents "Expired"
  //   { label: "Suspended (S)", value: "S" }, // Represents "Suspended"
  //   { label: "Withdrawn (W)", value: "W" }, // Represents "Withdrawn"
  // ];

  // const conformityStatuses: OptionType[] = [
  //   { label: "Critical non-conformity (C)", value: "C" }, // Represents "Critical non-conformity"
  //   { label: "Non-conformity (N)", value: "N" }, // Represents "Non-conformity"
  //   { label: "Administrative (A)", value: "A" }, // Represents "Administrative"
  //   { label: "Other (O)", value: "O" }, // Represents "Other"
  // ];

  // Filter status options based on scValidFromDate
  const allowedFutureStatuses = ["I", "V"];
  const filteredStatusOptions: OptionType[] = isFutureDate
    ? statusOptions.filter((option) =>
        allowedFutureStatuses.includes(String(option.value))
      ) // Only Issued and Valid for future dates
    : statusOptions; // All options for past/present dates

  // Check if scNumberOfFarms field should be enabled based on selected standards
  const farmRelatedStandards = ["RWS", "RMS", "RAS", "RDS", "MMS"];
  const hasFarmRelatedStandards = scStandards.some((standard) =>
    farmRelatedStandards.includes(standard)
  );

  // Fetch dropdown options from API
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        setLoading(true);

        // Fetch SC Status options
        const scStatusResponse = await dtrackItServiceV4.getSCStatusV4();
        if (scStatusResponse?.data) {
          const transformedStatusOptions = scStatusResponse.data.map(
            (item: any) => ({
              label: `${item.description_1} (${item.code_1})`,
              value: item.code_1,
            })
          );
          setStatusOptions(transformedStatusOptions);
        }

        // Fetch Suspension Reason options
        const suspensionReasonResponse =
          await dtrackItServiceV4.getSuspensionReason();
        if (suspensionReasonResponse?.data) {
          const transformedSuspensionOptions =
            suspensionReasonResponse.data.map((item: any) => ({
              label: `${item.description_1} (${item.code_1})`,
              value: item.code_1,
            }));
          setSuspensionReasonOptions(transformedSuspensionOptions);
        }
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
        notify("Failed to load dropdown options", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchDropdownOptions();
  }, []);

  const isNaLike = (v?: string | null) => {
    if (!v) return false;
    const lower = v.trim().toLowerCase();
    return ["na", "n/a", "na.", "na ", "not available", "null", "nil", "none", "n a", "n.a"].includes(lower);
  };

  const naOnlyValidator = (value?: string | null) => {
    if (!value) return true;
    const lower = value.trim().toLowerCase();
    if (!isNaLike(value)) return true;
    return lower === "na";
  };
  const formConfig = {
    pageName: "scope-certificate-details",
    initialValues: detailsData,
    validationSchema: Yup.object().shape({
      scopeCertificateNo: Yup.string()
        .required("Scope Certificate Number is required")
        .length(19, "Must be exactly 23 characters")
        .matches(/^SC-\d{8}-\d{4}-00$/, "Invalid format (XXX-SC-########-YYMM-00)"),
      brandName: Yup.array().of(Yup.string().max(100, "Max 100 characters per brand")),
      gotsSCNo: Yup.string().max(100, "Max 100 characters"),
      scLegacyNo: Yup.array()
        .of(
          Yup.string()
            .max(100, "Max 100 characters")
            .test(
              "na-only",
              'Only lowercase "na" is allowed for NA values',
              (value) => naOnlyValidator(value)
            )
        )
        .min(1, "SC Legacy Number is required"),
      scCBAuditorId: Yup.array()
        .of(
          Yup.string()
            .max(10, "Max 10 characters")
            .matches(/^TEU-\d{6}$/, "Format TEU-######")
        ),
      scStandardProgram: Yup.string(),
      scStatus: Yup.string().required("SC Status is required"),
      scStatusDescription: Yup.string(),
      // scWithdrawalReason: Yup.string().when('scStatus', {
      //   is: 'W',
      //   then: (schema) => schema.required('Withdrawal Reason is required'),
      //   otherwise: (schema) => schema.optional()
      // }),
      scSuspensionReason: Yup.string().when("scStatus", {
        is: "S",
        then: (schema) => schema.required("Suspension Reason is required"),
        otherwise: (schema) => schema.optional(),
      }),
      scSuspensionReasonDescription: Yup.string(),
      scNumberOfFarms: Yup.number()
        .nullable()
        .when([], {
          is: () => hasFarmRelatedStandards,
          then: (schema) =>
            schema.required(
              "SC Number of Farms is required when farm-related standards are selected"
            ),
          otherwise: (schema) => schema.optional(),
        }),
      scVersionNo: Yup.string()
        .required("SC Version Number is required")
        .length(19, "Must be exactly 23 characters")
        .matches(/^SC-\d{8}-\d{4}-\d{2}$/, "Invalid format. Must be of format (XXX-SC-########-YYMM-NN)"),
      scProductLimitations: Yup.string().max(2000, "Max 2000 characters"),
      ccsFlag: Yup.string().required("Choose whether CCS Applies"),
      stdClaimsPolicy: Yup.string().required(),
      // globalComment: Yup.string(),
    }),
    readOnly: hasSubmitSC,
    sections: [
      {
        // title: 'Scope Certificate Details',
        fields: [
          {
            label: "SC Number",
            name: "scopeCertificateNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled:
              editSCInfo?.scId === SC_ID && editSCInfo?.isParentStatus
                ? true
                : false,
            props: {
              placeholder: "Enter Scope Certificate Number",
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <>
                      <strong>{shortCode}-</strong>
                    </>
                  </InputAdornment>
                ),
              },
            },
            events: {
              onChange: (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                setFieldValue("scVersionNo", updatedValue);
              },
            },
          },
          {
            label: "SC Version Number",
            name: "scVersionNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: false,
            props: {
              placeholder: "Enter SC Version Number",
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <>
                      <strong>{shortCode}-</strong>
                    </>
                  </InputAdornment>
                ),
              },
            },
          },

          {
            label: "GOTS SC Number",
            name: "gotsSCNo",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: { placeholder: "Enter GOTS SC Number" },
          },
          {
            label: "Brand Name",
            name: "brandName",
            type: "chips",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              placeholder: "Enter Brand Name(s)",
            },
          },

          {
            label: "SC CB Auditor ID",
            name: "scCBAuditorId",
            type: "chips",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              placeholder: "Enter CB Auditor ID",
            },
          },
          {
            label: "SC Legacy Number",
            name: "scLegacyNo",
            type: "chips",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: true,
            helperText:
              'Please fill "na" if SC Legacy Number is not applicable',
            props: { placeholder: "Enter SC Legacy Number" },
          },
          // {
          //   label: 'SC Standard Program',
          //   name: 'scStandardProgram',
          //   type: 'select',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: false,
          //   props: {
          //     options: [
          //       { label: "None", value: "" },
          //       { label: "Responsible Animal Fiber", value: "RAS,RWS,RAS" },
          //       { label: "Content Claim Standard", value: "CCS" },
          //     ],
          //     placeholder: 'Select Standard Program',
          //   },
          // },
          {
            label: "SC Status",
            name: "scStatus",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              options: filteredStatusOptions,
              placeholder: "Select SC Status",
            },
            events: {
              onChange: (
                value: any,
                formValues: any,
                setFieldValue: (field: string, value: any) => void
              ) => {
                // Reset suspension reason if status is not 'S'
                if (value !== "S") {
                  setFieldValue("scSuspensionReason", "");
                  setFieldValue("scSuspensionReasonDescription", "");
                }
                // Store the description for the selected status
                const selectedOption = filteredStatusOptions.find(
                  (option) => option.value === value
                );
                if (selectedOption) {
                  setFieldValue("scStatusDescription", selectedOption.label);
                }
              },
            },
          },
          // {
          //   label: 'SC Withdrawal Reason',
          //   name: 'scWithdrawalReason',
          //   type: 'select',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: (values: any) => values.scStatus === "W",
          //   disabled: (values: any) => values.scStatus !== "W",
          //   visible: (values: any) => values.scStatus === "W",
          //   props: {
          //     options: certificationStatuses,
          //     placeholder: 'Select Withdrawal Reason (if applicable)',
          //   },
          // },
          {
            label: "SC Suspension Reason",
            name: "scSuspensionReason",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: (values: any) => values.scStatus === "S",
            disabled: (values: any) => values.scStatus !== "S",
            visible: (values: any) => values.scStatus === "S",
            props: {
              options: suspensionReasonOptions,
              placeholder: "Select Suspension Reason (if applicable)",
            },
            events: {
              onChange: (
                value: any,
                formValues: any,
                setFieldValue: (field: string, value: any) => void
              ) => {
                // Store the description for the selected suspension reason
                const selectedOption = suspensionReasonOptions.find(
                  (option) => option.value === value
                );
                if (selectedOption) {
                  setFieldValue(
                    "scSuspensionReasonDescription",
                    selectedOption.label
                  );
                }
              },
            },
          },
          {
            label: "SC Number of Farms",
            name: "scNumberOfFarms",
            type: "number",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: (values: any) => hasFarmRelatedStandards,
            // disabled: (values: any) => !hasFarmRelatedStandards, Always visible but disabled when no farm standards
            props: {
              placeholder: hasFarmRelatedStandards
                ? "Enter Number of Farms"
                : "Select farm-related standards first",
              // helperText: hasFarmRelatedStandards
              //   ? 'Required when farm-related standards (RWS, RMS, RAS, RDS, MMS) are selected'
              //   : 'This field is only available when farm-related standards are selected in Step 2'
            },
            events: {
              onChange: (
                value: any,
                formValues: any,
                setFieldValue: (field: string, value: any) => void
              ) => {
                // Convert string to number for proper handling
                const numericValue = value === "" ? null : Number(value);
                console.log("scNumberOfFarms onChange:", {
                  value,
                  numericValue,
                  hasFarmRelatedStandards,
                });
                setFieldValue("scNumberOfFarms", numericValue);
              },
            },
          },
          {
            label: "SC Product Limitations",
            name: "scProductLimitations",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: {
              placeholder: "Enter Product Limitations",
            },
          },
          {
            label: "Does CCS Apply",
            name: "ccsFlag",
            type: "radio",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
            },
          },
          {
            label: "Textile Exchange Standards Claims Policy",
            name: "stdClaimsPolicy",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              placeholder: "Select the version number",
              options: [
                { value: "V1.3", label: "V1.3" },
                { value: "V1.4", label: "V1.4" },
              ],
            },
          },
          // {
          //   label: 'Global Comment Not For Actual Data To Be Provided',
          //   name: 'globalComment',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: false,
          //   props: { placeholder: 'Enter Global Comment' },
          // },
          // Removed: certifiedOrganizationApprovedClaims field - moved to Standard Details tab
        ],
      },
    ],
    handleSubmit: (values: any) => {
      console.log("Form submission values:", values);
      console.log("scNumberOfFarms value:", values.scNumberOfFarms);
      setDetailsData(values);
    },
  };

  return (
    <FormBuilder
      formConfig={formConfig}
      onSubmit={formConfig.handleSubmit}
      formRef={formRef}
    />
  );
};

export default ScopeCertificateDetailsForm;
